import { ACCOUNTTYPE } from '../account-type.enum';
import { defaultZoneConfiguration, WidgetNames } from '../config.type';
import { CanvasZone, RightWidgetConfiguration } from '../workflow.type';
import { CommonPageConfig, WorkflowPageConfig, WorkflowZoneDevicesConfig } from '../workflow.v2.type';
import { ZoneInfo } from '../zone-info.utils';
import { Constants, cryptoRandomUUID } from './workflow-constants.type';

export function defaultCanvas(): CanvasZone {
  return {
    canvases: {
      name: 'Canvas',
      subscription: {
        default: {
          mobile: {
            zoneTypeConfig: 'new',
            zoneConfiguration: defaultZoneConfiguration,
            url: '',
            pages: [buildEmptyPageConfigForDynamicGridPage('Page 01', 'Page 01')],
          },
          desktop: {
            zoneTypeConfig: 'new',
            url: '',
            zoneConfiguration: defaultZoneConfiguration,
            pages: [buildEmptyPageConfigForDynamicGridPage('Page 01', 'Page 01')],
          },
          tablet: {
            zoneTypeConfig: 'new',
            zoneConfiguration: defaultZoneConfiguration,
            url: '',
            pages: [buildEmptyPageConfigForDynamicGridPage('Page 01', 'Page 01')],
          },
        } as WorkflowZoneDevicesConfig,
      },
    },
  };
}

export const defaultPageConfig = {
  mainComponentPageName: 'ParentContainerComponent',
  isRemovable: true,
  isUsingHtmlStructure: true,
  showHeader: false,
  labelsAsPlaceholder: false,
  backgroundColor: 'var(--color-background)',
  backgroundImage: {
    image: '',
    opacity: '1',
  },
  backgroundPosition: {
    x: 'unset',
    y: 'unset',
  },
  pageType: 'dynamicGrid',
  backgroundSize: 'auto',
  backgroundGradient: '',
  fontsConfig: {
    h1: {
      'font-family': 'var(--font-family)',
      'font-weight': 'regular',
    },
    h2: {
      'font-family': 'var(--font-family)',
      'font-weight': 'regular',
    },
    h3: {
      'font-family': 'var(--font-family)',
      'font-weight': 'regular',
    },
    bodyFont: {
      'font-family': 'var(--font-family)',
      'font-weight': 'regular',
    },
  },
  hideNavigationMenu: false,
};

export const commonPageConfigValue = {
  gridsterMargin: {
    left: 1,
    right: 1,
    top: 2,
    unit: 'rem',
  },
  pageMargin: {
    left: 1,
    right: 1,
    top: 2,
    unit: 'rem',
  },
  showHeader: true,
  labelsAsPlaceholder: false,
  backgroundColor: 'var(--color-background)',
  backgroundImage: {
    image: '',
    opacity: '1',
  },
  backgroundPosition: {
    x: 'unset',
    y: 'unset',
  },
  backgroundSize: 'auto',
  backgroundGradient: '',
  fontsConfig: {
    h1: {
      'font-family': 'var(--font-family)',
      'font-weight': 'regular',
    },
    h2: {
      'font-family': 'var(--font-family)',
      'font-weight': 'regular',
    },
    h3: {
      'font-family': 'var(--font-family)',
      'font-weight': 'regular',
    },
    bodyFont: {
      'font-family': 'var(--font-family)',
      'font-weight': 'regular',
    },
  },
  hideNavigationMenu: false,
};

export function buildEmptyPageConfigForDynamicGridPage(
  pageName: string,
  pageTitle: string,
  pageConfiguration: CommonPageConfig = {
    mainComponentPageName: 'ParentContainerComponent',
    isRemovable: true,
    url: formatNameAsUrl(pageName),
    pageType: 'dynamicGrid',
    isUsingHtmlStructure: true,
    ...commonPageConfigValue,
  },
): WorkflowPageConfig {
  return {
    name: pageName,
    title: pageTitle,
    pageUniqueId: cryptoRandomUUID(),
    pageConfiguration: pageConfiguration,
    widgets: [],
  };
}

/**
 * To remove hyphen, underscore from zone or page name.
 * @param paramName - zone or page name
 */
export function formatNameAsUrl(paramName: string): string {
  if (!paramName) {
    return '';
  }
  const words = paramName.split(/[-_ ]/);
  return words
    .map((word) => word.toLowerCase())
    .filter((word) => word !== 'zone')
    .join('-');
}

export type defaultModules = {
  [key: string]: ZoneInfo;
};

export type widgetConfigResetType = {
  [key: string]: RightWidgetConfiguration;
};

export const getWidgetConfigForReset = {
  // signup widgets

  [WidgetNames.BIRTHDAY_WIDGET]: {
    elementsConfig: [
      {
        field_name: 'min_age',
        type: '18',
        show: true,
      },
    ],
  } as RightWidgetConfiguration,

  [WidgetNames.PHONE_WIDGET]: {
    elementsConfig: [
      {
        field_name: 'verificationViaOTP',
        show: true,
      },
    ],
  } as RightWidgetConfiguration,

  [WidgetNames.EMAIL_WIDGET]: {
    elementsConfig: [
      {
        field_name: 'verificationViaOTP',
        show: true,
      },
    ],
  } as RightWidgetConfiguration,

  // Display Widgets

  [WidgetNames.LINE_WIDGET]: {
    attributes: {
      field_attributes: [
        {
          additional_config: {
            show: true,
            color: 'var(--color-on-background)',
            paddingTop: 20,
            paddingBottom: 20,
            lineThickness: 1,
            lineWidth: 90,
            contentAlignment: 'center',
          },
        },
      ],
    },
  } as RightWidgetConfiguration,

  [WidgetNames.IFRAME_WIDGET]: {
    attributes: {
      css_config: {
        iframeUrl: '',
        verticalHeight: '30',
        showIFrameWidget: true,
      },
    },
  } as RightWidgetConfiguration,

  [WidgetNames.GOCARDLESS_COMPLIANCE_WIDGET]: {
    attributes: {
      css_config: {},
    },
  } as RightWidgetConfiguration,

  // kyc

  [WidgetNames.CTA_BUTTON_WIDGET]: {
    attributes: {
      widgetProperties: {
        textContent: 'KYC ME!',
      },
    },
  } as RightWidgetConfiguration,

  // landing page

  [WidgetNames.QUICK_LINKS_WIDGET]: {
    attributes: {
      css_config: {
        ...Constants.defaultBaseCssConfig,
        textAlignment: 'center',
        verticalAlignment: 'center',
        fontSize: 20,
        shadowBlur: 0,
        shadowSpread: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
      },
      widgetProperties: {
        textContent: 'Quick Links',
        icon: '',
      },
    },
  } as RightWidgetConfiguration,

  [WidgetNames.MULTI_ICON_QUICK_LINK_WIDGET]: {
    attributes: {
      css_config: {
        ...Constants.defaultBaseCssConfig,
        textAlignment: 'left',
        verticalAlignment: 'center',
        fontSize: 20,
        shadowBlur: 0,
        shadowSpread: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
      },
      widgetProperties: {
        textContent: 'Multi Icon Quick Link ',
        icon: '',
      },
    },
  } as RightWidgetConfiguration,

  [WidgetNames.BACK_BUTTON_WIDGET]: {
    attributes: {
      css_config: {
        ...Constants.defaultBaseCssConfig,
        textAlignment: 'center',
        verticalAlignment: 'center',
        shadowBlur: 0,
        shadowSpread: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        iconSize: 24,
        borderWidth: 1,
      },
      widgetProperties: {
        textContent: 'Back',
        icon: '',
      },
    },
  } as RightWidgetConfiguration,

  [WidgetNames.FORM_SUBMISSION_BUTTON]: {
    attributes: {
      css_config: {
        ...Constants.defaultBaseCssConfig,
        textAlignment: 'center',
        backGroundColor: 'var(--color-primary)',
        color: 'var(--color-surface)',
        fontSize: 16,
        shadowBlur: 0,
        shadowSpread: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        borderRadiusTopLeft: 5,
        borderRadiusBottomLeft: 5,
        borderRadiusTopRight: 5,
        borderRadiusBottomRight: 5,
      },
      widgetProperties: {
        textContent: 'CTA',
        icon: '',
      },
    },
  } as RightWidgetConfiguration,

  [WidgetNames.ONBOARDING_BACK_NEXT_BUTTON]: {
    attributes: {
      css_config: {
        ...Constants.defaultBaseCssConfig,
        backGroundColor: 'var(--color-primary)',
        color: 'var(--color-surface)',
        textAlignment: 'center',
        verticalAlignment: 'center',
        paddingTop: 20,
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 20,
      },
      widgetProperties: {
        textContent: 'Next',
        icon: '',
      },
    },
  } as RightWidgetConfiguration,

  [WidgetNames.MANDATORY_CTA_BUTTON]: {
    attributes: {
      css_config: {
        ...Constants.defaultBaseCssConfig,
        textAlignment: 'center',
        verticalAlignment: 'center',
        backGroundColor: 'var(--color-primary)',
        color: 'var(--color-surface)',
        fontSize: 16,
        shadowBlur: 0,
        shadowSpread: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        borderRadiusTopLeft: 5,
        borderRadiusBottomLeft: 5,
        borderRadiusTopRight: 5,
        borderRadiusBottomRight: 5,
      },
      widgetProperties: {
        textContent: 'CTA',
        icon: '',
      },
    },
  } as RightWidgetConfiguration,

  [WidgetNames.WIDGET_NESTED_BUTTON]: {
    attributes: {
      css_config: {
        ...Constants.defaultUiNestedButtonCssConfig,
        textAlignment: 'center',
        verticalAlignment: 'center',
        backGroundColor: 'var(--color-primary)',
        color: 'var(--color-surface)',
      },
      widgetProperties: {
        textContent: 'Button',
        icon: '',
      },
    },
  } as RightWidgetConfiguration,

  [WidgetNames.RESEND_WIDGET]: {
    attributes: {
      css_config: {
        ...Constants.defaultBaseCssConfig,
        textAlignment: 'center',
        verticalAlignment: 'center',
        backGroundColor: 'var(--color-primary)',
        color: 'var(--color-surface)',
        fontSize: 16,
        shadowBlur: 0,
        shadowSpread: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        borderRadiusTopLeft: 5,
        borderRadiusBottomLeft: 5,
        borderRadiusTopRight: 5,
        borderRadiusBottomRight: 5,
        paddingTop: 15,
        paddingBottom: 15,
      },
      widgetProperties: {
        textContent: 'Resend Link',
        icon: '',
      },
    },
  } as RightWidgetConfiguration,

  [WidgetNames.TEXT_WIDGET]: {
    attributes: {
      css_config: {
        ...Constants.defaultBaseCssConfig,
        textAlignment: 'center',
        verticalAlignment: 'center',
        fontSize: 16,
        color: 'var(--color-on-background)',
      },
      widgetProperties: {
        textContent: 'Text / HTML',
      },
    },
  } as RightWidgetConfiguration,

  // menu zone
} as widgetConfigResetType;

const blankDestinationIdentifiers = {
  accountNumber: '',
  sortCode: '',
  currency: '',
  country: '',
  iban: '',
  bic: '',
  aba: '',
  rtnCanada: '',
  clabe: '',
  bsbCode: '',
  ifsc: '',
  cnaps: '',
};
export const defaultBeneficiaryList = [
  {
    id: '1',
    name: 'Backling Jr.',
    accountType: ACCOUNTTYPE.PERSONAL,
    destinationIdentifier: blankDestinationIdentifiers,
  },
  {
    id: '2',
    name: 'Frank Fisher',
    accountType: ACCOUNTTYPE.PERSONAL,
    destinationIdentifier: blankDestinationIdentifiers,
  },
  {
    id: '3',
    name: 'Mr Chapman',
    accountType: ACCOUNTTYPE.BUSINESS,
    destinationIdentifier: blankDestinationIdentifiers,
  },
];
